import type {
  Diff,
  DoorParams,
  Drawing,
  IdsReport,
  MeshData,
  PlanOptions,
  Point2,
  RawAttribute,
  RawOptions,
  RoofParams,
  Schema,
  SectionOptions,
  SiteInfo,
  SlabParams,
  SpaceParams,
  SpatialAnchor,
  WallParams,
  WallSegment,
  WindowParams,
} from '@manudev/model';
import type { PropsElement, Storey } from '@manudev/assistant';
import { create } from 'zustand';
import type { WireDrawing, WireMesh, WorkerEvent, WorkerRequest } from './store.worker';

export type HeroStoreStatus = 'idle' | 'loading' | 'ready' | 'error';

interface HeroStoreState {
  status: HeroStoreStatus;
  error: string | null;
  // counts the edits that reached the snapshot, so a check can tell it is stale
  version: number;
  schema: Schema | null;
  site: SiteInfo | null;
  storeys: Storey[];
  elements: PropsElement[];
  newIds: number[];
  meshes: MeshData[];
  load: (url: string) => void;
  addWall: (storeyId: number, params: WallParams) => Promise<number>;
  addSlab: (storeyId: number, params: SlabParams) => Promise<number>;
  addRoof: (storeyId: number, params: RoofParams) => Promise<number>;
  addDoor: (storeyId: number, params: DoorParams) => Promise<number>;
  addWindow: (storeyId: number, params: WindowParams) => Promise<number>;
  addSpace: (storeyId: number, params: SpaceParams) => Promise<number>;
  addStorey: (name: string, elevation: number) => Promise<number>;
  addRaw: (entityType: string, attributes: RawAttribute[], opts?: RawOptions) => Promise<number>;
  materialId: (name: string) => Promise<number | null>;
  anchor: (storeyId: number) => Promise<SpatialAnchor>;
  wallSegments: (storeyId: number) => Promise<WallSegment[]>;
  spaceFootprints: (storeyId: number) => Promise<Point2[][]>;
  guid: () => Promise<string>;
  beginBatch: () => Promise<void>;
  endBatch: () => Promise<void>;
  remove: (id: number) => Promise<void>;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  mesh: (ids?: number[]) => Promise<MeshData[]>;
  exportStep: () => Promise<Uint8Array>;
  plan: (opts: PlanOptions) => Promise<Drawing>;
  section: (opts: SectionOptions) => Promise<Drawing>;
  checkIds: (idsXml: string) => Promise<IdsReport>;
  diff: () => Promise<Diff>;
}

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<
  number,
  { resolve: (e: WorkerEvent) => void; reject: (err: Error) => void }
>();

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./store.worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = ({ data }: MessageEvent<WorkerEvent>) => {
    const p = pending.get(data.requestId);
    pending.delete(data.requestId);
    if (!p) return;
    if (data.type === 'error') p.reject(new Error(data.message));
    else p.resolve(data);
  };
  worker.onerror = (e) => {
    for (const p of pending.values()) p.reject(new Error(e.message));
    pending.clear();
  };
  return worker;
}

// Plain Omit collapses a union to its common keys; this keeps each request variant distinct.
type WithoutRequestId<T> = T extends { requestId: number } ? Omit<T, 'requestId'> : never;

function call(req: WithoutRequestId<WorkerRequest>): Promise<WorkerEvent> {
  const w = ensureWorker();
  const requestId = nextId++;
  return new Promise((resolve, reject) => {
    pending.set(requestId, { resolve, reject });
    w.postMessage({ ...req, requestId } as WorkerRequest);
  });
}

function fromWire(m: WireMesh): MeshData {
  return m as MeshData;
}

function fromWireDrawing(d: WireDrawing): Drawing {
  const decoder = new TextDecoder();
  return {
    svg: decoder.decode(d.svg),
    dxf: () => decoder.decode(d.dxf),
    bounds: d.bounds,
    rooms: d.rooms,
  };
}

async function drawing(req: WithoutRequestId<WorkerRequest>): Promise<Drawing> {
  const e = (await call(req)) as Extract<WorkerEvent, { type: 'drawing' }>;
  return fromWireDrawing(e.drawing);
}

// An edit made inside a batch answers with its id alone; the batch's end carries the snapshot.
async function edit(
  set: (s: (state: HeroStoreState) => Partial<HeroStoreState>) => void,
  req: WithoutRequestId<WorkerRequest>,
): Promise<number> {
  const e = await call(req);
  if (e.type === 'edit')
    set((s) => ({
      version: s.version + 1,
      storeys: e.storeys,
      elements: e.elements,
      newIds: e.newIds,
      meshes: e.meshes.map(fromWire),
    }));
  return (e.type === 'edit' || e.type === 'id' ? e.id : null) ?? -1;
}

export const useHeroStore = create<HeroStoreState>((set, get) => ({
  status: 'idle',
  error: null,
  version: 0,
  schema: null,
  site: null,
  storeys: [],
  elements: [],
  newIds: [],
  meshes: [],
  load: (url) => {
    if (get().status !== 'idle' && get().status !== 'error') return;
    set({ status: 'loading', error: null });
    call({ type: 'load', url })
      .then((e) => {
        const loaded = e as Extract<WorkerEvent, { type: 'loaded' }>;
        set({
          status: 'ready',
          schema: loaded.schema,
          site: loaded.site,
          storeys: loaded.storeys,
          elements: loaded.elements,
        });
      })
      .catch((err: Error) => set({ status: 'error', error: err.message }));
  },
  addWall: (storeyId, params) => edit(set, { type: 'addWall', storeyId, params }),
  addSlab: (storeyId, params) => edit(set, { type: 'addSlab', storeyId, params }),
  addRoof: (storeyId, params) => edit(set, { type: 'addRoof', storeyId, params }),
  addDoor: (storeyId, params) => edit(set, { type: 'addDoor', storeyId, params }),
  addWindow: (storeyId, params) => edit(set, { type: 'addWindow', storeyId, params }),
  addSpace: (storeyId, params) => edit(set, { type: 'addSpace', storeyId, params }),
  addStorey: (name, elevation) => edit(set, { type: 'addStorey', name, elevation }),
  addRaw: (entityType, attributes, opts) =>
    edit(set, { type: 'addRaw', entityType, attributes, opts }),
  materialId: async (name) => {
    const e = (await call({ type: 'materialId', name })) as Extract<
      WorkerEvent,
      { type: 'materialId' }
    >;
    return e.id;
  },
  anchor: async (storeyId) => {
    const e = (await call({ type: 'anchor', storeyId })) as Extract<
      WorkerEvent,
      { type: 'anchor' }
    >;
    return e.anchor;
  },
  wallSegments: async (storeyId) => {
    const e = (await call({ type: 'wallSegments', storeyId })) as Extract<
      WorkerEvent,
      { type: 'wallSegments' }
    >;
    return e.segments;
  },
  spaceFootprints: async (storeyId) => {
    const e = (await call({ type: 'spaceFootprints', storeyId })) as Extract<
      WorkerEvent,
      { type: 'spaceFootprints' }
    >;
    return e.rings;
  },
  guid: async () => {
    const e = (await call({ type: 'guid' })) as Extract<WorkerEvent, { type: 'guid' }>;
    return e.guid;
  },
  beginBatch: async () => {
    await edit(set, { type: 'beginBatch' });
  },
  endBatch: async () => {
    await edit(set, { type: 'endBatch' });
  },
  remove: async (id) => {
    await edit(set, { type: 'remove', id });
  },
  undo: async () => {
    await edit(set, { type: 'undo' });
  },
  redo: async () => {
    await edit(set, { type: 'redo' });
  },
  mesh: async (ids) => {
    const e = (await call({ type: 'mesh', ids })) as Extract<WorkerEvent, { type: 'mesh' }>;
    return e.meshes.map(fromWire);
  },
  exportStep: async () => {
    const e = (await call({ type: 'exportStep' })) as Extract<WorkerEvent, { type: 'exportStep' }>;
    return e.bytes;
  },
  plan: (opts) => drawing({ type: 'plan', opts }),
  section: (opts) => drawing({ type: 'section', opts }),
  checkIds: async (idsXml) => {
    const e = (await call({ type: 'checkIds', idsXml })) as Extract<WorkerEvent, { type: 'ids' }>;
    return e.report;
  },
  diff: async () => {
    const e = (await call({ type: 'diff' })) as Extract<WorkerEvent, { type: 'diff' }>;
    return e.diff;
  },
}));
